import { useDeferredValue, useMemo, useState } from "react";
import { Search } from "lucide-react";
import type { Stream } from "../lib/types";
import { useIsPhone } from "../lib/useIsPhone";
import { ChannelRow } from "./ChannelRow";
import { StreamCard } from "./StreamCard";

interface Props {
  streams: Stream[];
  layout?: "landscape" | "portrait";
  emptyText?: string;
  pageSize?: number;
  searchPlaceholder?: string;
  hrefFor?: (s: Stream) => string;
}

const norm = (s: string) => s.toLowerCase().normalize("NFKD").replace(/[\u0300-\u036f]/g, "");

export function StreamGrid({
  streams,
  layout = "portrait",
  emptyText = "Nothing here yet.",
  pageSize = 60,
  searchPlaceholder = "Filter…",
  hrefFor,
}: Props) {
  const [q, setQ] = useState("");
  const dq = useDeferredValue(q);
  const isPhone = useIsPhone();
  const [page, setPage] = useState(1);

  const filtered = useMemo(() => {
    if (!dq.trim()) return streams;
    const needle = norm(dq.trim());
    return streams.filter((s) => norm(s.name || "").includes(needle));
  }, [streams, dq]);

  const visible = filtered.slice(0, page * pageSize);

  return (
    <div>
      <div className="mb-4 flex items-center gap-2 max-w-md">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
          <input
            type="text"
            placeholder={searchPlaceholder}
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setPage(1);
            }}
            className="w-full h-10 bg-surface border border-muted/40 rounded-lg pl-9 pr-3 text-[13px] font-ui text-text-primary placeholder:text-text-muted gold-focus transition-fast"
          />
        </div>
        <span className="text-[11px] font-mono text-text-secondary uppercase tracking-[0.04em]">
          {filtered.length.toLocaleString()} items
        </span>
      </div>

      {visible.length === 0 ? (
        <p className="text-[14px] font-ui text-text-secondary py-12 text-center">
          {emptyText}
        </p>
      ) : (
        <>
          {isPhone && layout === "landscape" ? (
            /* Phone Live TV is a list, not a logo grid. A 100px tile cannot
               carry "UK: SKY SPORTS MAIN EVENT FHD" plus a now-playing line. */
            <div className="flex flex-col gap-2">
              {visible.map((s) => (
                <ChannelRow
                  key={`${s.kind}-${s.id}`}
                  stream={s}
                  href={hrefFor ? hrefFor(s) : `/watch/${s.kind}/${s.id}`}
                />
              ))}
            </div>
          ) : (
            <div
              className={`grid gap-3 ${
                layout === "landscape"
                  ? "grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5"
                  : "grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-8"
              }`}
            >
              {visible.map((s) => (
                <StreamCard
                  key={`${s.kind}-${s.id}`}
                  stream={s}
                  layout={layout}
                  href={hrefFor ? hrefFor(s) : undefined}
                />
              ))}
            </div>
          )}
          {visible.length < filtered.length && (
            <div className="mt-6 flex justify-center">
              <button
                onClick={() => setPage((p) => p + 1)}
                className="px-5 py-2.5 rounded-pill border border-muted/40 text-[12px] font-ui font-medium uppercase tracking-[0.04em] text-text-primary hover:border-accent-gold hover:text-accent-gold transition-fast"
              >
                Load more
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
